import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Users, FileText, Mail, Plus, Trash2, MoreVertical, LogOut, Home } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'tenant' });
  const [error, setError] = useState('');

  const token = user?.token;

  const fetchData = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [u, q, c] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/quotes`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { headers, withCredentials: true }),
      ]);
      setUsers(u.data);
      setQuotes(q.data);
      setContacts(c.data);
    } catch (err) {
      console.error('Fetch error', err);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCreateOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'tenant' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchData();
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchData();
    } catch (err) {
      console.error('Update error', err);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchData();
    } catch (err) {
      console.error('Delete quote error', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const roleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      landlord: 'bg-blue-100 text-blue-800',
      tenant: 'bg-emerald-100 text-emerald-800',
    };
    return <Badge className={`${colors[role] || 'bg-stone-100 text-stone-800'} text-xs font-medium`}>{role}</Badge>;
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="admin-dashboard">
      {/* Sub header */}
      <div className="bg-white border-b border-stone-200 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <h1 className="font-outfit font-bold text-stone-900 text-lg">Admin Dashboard</h1>
            <Badge className="bg-red-100 text-red-800 text-xs">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="admin-home-btn">
              <Home className="w-4 h-4 mr-1" /> Home
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="stat-users">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-900" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-bold text-stone-900">{users.length}</p>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="stat-quotes">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-bold text-stone-900">{quotes.length}</p>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Quote Requests</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="stat-contacts">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-bold text-stone-900">{contacts.length}</p>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Messages</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-stone-100 rounded-xl p-1 mb-6">
            <TabsTrigger value="users" className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-1.5" /> Users
            </TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="admin-tab-quotes">
              <FileText className="w-4 h-4 mr-1.5" /> Quotes
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="admin-tab-contacts">
              <Mail className="w-4 h-4 mr-1.5" /> Messages
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
              <div className="flex items-center justify-between p-5 border-b border-stone-100">
                <h2 className="font-outfit font-semibold text-stone-900">User Management</h2>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full text-sm" data-testid="create-user-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-outfit">Create New User</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-emerald-900/70 mb-1 block">Name</Label>
                        <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required className="text-stone-900" data-testid="new-user-name" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-emerald-900/70 mb-1 block">Email</Label>
                        <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required className="text-stone-900" data-testid="new-user-email" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-emerald-900/70 mb-1 block">Password</Label>
                        <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required className="text-stone-900" data-testid="new-user-password" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-emerald-900/70 mb-1 block">Role</Label>
                        <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                          <SelectTrigger data-testid="new-user-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tenant">Tenant</SelectItem>
                            <SelectItem value="landlord">Landlord</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {error && <p className="text-red-600 text-sm">{error}</p>}
                      <Button type="submit" className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" data-testid="submit-create-user">
                        Create User
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                      <TableCell className="font-medium text-stone-900">{u.name}</TableCell>
                      <TableCell className="text-stone-600">{u.email}</TableCell>
                      <TableCell>{roleBadge(u.role)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`user-actions-${u._id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateRole(u._id, 'tenant')}>Set Tenant</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateRole(u._id, 'landlord')}>Set Landlord</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateRole(u._id, 'admin')}>Set Admin</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteUser(u._id)} data-testid={`delete-user-${u._id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
              <div className="p-5 border-b border-stone-100">
                <h2 className="font-outfit font-semibold text-stone-900">Quote Requests</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Property</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Service</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((q) => (
                    <TableRow key={q._id} data-testid={`quote-row-${q._id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-stone-900">{q.name}</p>
                          <p className="text-xs text-stone-500">{q.email} | {q.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-stone-700 text-sm">{q.property_address}</p>
                          <p className="text-xs text-stone-500">{q.property_type}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600 text-sm">{q.service_needed}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${q.status === 'pending' ? 'bg-amber-100 text-amber-800' : q.status === 'reviewed' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {q.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteQuote(q._id)} data-testid={`delete-quote-${q._id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {quotes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-stone-500 py-8">No quote requests yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
              <div className="p-5 border-b border-stone-100">
                <h2 className="font-outfit font-semibold text-stone-900">Contact Messages</h2>
              </div>
              <div className="divide-y divide-stone-100">
                {contacts.map((c) => (
                  <div key={c._id} className="p-5" data-testid={`contact-msg-${c._id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-stone-900">{c.name}</p>
                        <p className="text-xs text-stone-500">{c.email}</p>
                      </div>
                      <p className="text-xs text-stone-400">{c.created_at?.split('T')[0]}</p>
                    </div>
                    <p className="text-sm font-medium text-stone-800 mb-1">{c.subject}</p>
                    <p className="text-sm text-stone-600">{c.message}</p>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <div className="p-8 text-center text-stone-500">No messages yet</div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
